import { DateTime } from '@ksyos/date-time';
import {
    Box,
    Choicebox,
    Col,
    Form,
    FormGroup,
    FormGroupContext,
    FormSection,
    Radio,
    RadioGroup,
    Row,
    Select,
    Spacer,
    Text,
    TextArea,
    TextInput,
} from '@ksyos/design-system';
import React, { useEffect, useState } from 'react';
import type { IPerson } from '@common/utils/getDisplayName';
import type { IOsaseAsset, osasePressureUnit } from '@common/validation/customControls/somnology';
import type { IPractitioner } from '@frontend/components/interfaces';
import type { IOption } from '@frontend/components/interfaces/option';
import {
    EpisodeCategory,
    OsasComplaintBurden,
    OsasTrialPeriod,
    Profession,
    RequestStatus,
    ServiceCode,
    YesOrNoOrUnknown,
} from '@common/enums';
import { OsasPapType } from '@common/enums/osasPapType';
import { behandelingLabels } from '@common/interfaces/somnology/osase/treatmentsFields';
import { osaseFileExtensions } from '@common/utils/fileExtensions';
import { getDisplayName } from '@common/utils/getDisplayName';
import {
    osaseFilePlacement,
    osasePressureUnitOptions,
    OsasvcV2CTherapieGestartOption,
    OsasvcV2TherapieNamelijkOption,
} from '@common/validation/customControls/somnology';
import { IndependentDateInput } from '@frontend/components/fields';
import { fetchJson } from '@frontend/store/fetch';
import {
    emptyValidationErrors,
    getValidationErrorTranslation,
    scrollValidationErrorIntoCenterViewInScrollContainer,
    useEpisodeContext,
} from '@frontend/utils';
import type { ICustomControlRequestAddEditProps } from '../../../interfaces/customControlRequestAddEditProps';
import type { IOsasvcV2CustomControlData } from '../../../OSASVC/contact/v2/OsasvcV2RequestAddEdit';
import { getServiceLabel } from '../../../../../../../common/customControls/getServiceLabel';
import { FilesUrlBase } from '../../../../../../../common/enums/filesUrlBase';
import { ProcessEntity } from '../../../../../../../common/enums/processEntity';
import { ToastType } from '../../../../../../../common/enums/toastType';
import { getSelectedLanguage } from '../../../../../../translate';
import { showServiceToast } from '../../../../../../utils/showToast';
import { CancelButton } from '../../../../../elements/carePathButtons/CancelButton';
import { SaveButton } from '../../../../../elements/carePathButtons/SaveButton';
import { SaveSendButton } from '../../../../../elements/carePathButtons/SaveSendButton';
import { ButtonContainer } from '../../../../../elements/common';
import { FormContextProvider } from '../../../../../elements/formContext';
import { AssetDropzoneWithActions } from '../../../utils/v1/AssetDropzoneWithActions';
import { CareProviderSelector } from '../../../utils/v1/CareProviderSelector';
import { getCareProviderIsActive } from '../../../utils/v1/getCareProviderIsActive';
import { NewOrEditRequestProcessContainer } from '../../../utils/v1/NewOrEditRequestProcessContainer';

export interface IOsaseBehandeling {
    leefstijl_verandering: boolean;
    mra: boolean;
    pap: boolean;
    spt: boolean;
}

export interface IOsasePressure {
    unit: osasePressureUnit | null;
    value: string | null;
}

export interface IOsaseV2RequestCustomControlData {
    anamnese: string | null;
    behandelend_arts_id: string | null;
    behandelend_arts_name: string | null;
    behandeling: IOsaseBehandeling;
    beleid: string | null;
    contact_met_patient_gehad: boolean | null;
    files: IOsaseAsset[];
    huisarts_op_de_hoogte_brengen: boolean | null;
    klachtenlast: OsasComplaintBurden | null;
    leefstijl_volgt_patient_het_beleid: YesOrNoOrUnknown | null;
    mra_volgt_patient_het_beleid: YesOrNoOrUnknown | null;
    pap: {
        ahi: string | null;
        centraal_apneu: string | null;
        cheyne_stokes: string | null;
        druk: IOsasePressure;
        druk_epap: IOsasePressure;
        druk_ipap: IOsasePressure;
        extra_waarden_uit_rapport: string | null;
        gemiddeld_gebruik: string | null;
        maximale_druk: IOsasePressure;
        minimale_druk: IOsasePressure;
        nachten_met_ondersteuning: string | null;
        ninetyfive_druk: IOsasePressure;
        obstructief_apneu: string | null;
        periode_rapport_end: string | null;
        periode_rapport_start: string | null;
        spo2: string | null;
        type: OsasPapType | null;
    };
    proefperiode: OsasTrialPeriod | null;
    spt: {
        gemiddeld_gebruik: string | null;
        nachten_met_ondersteuning: string | null;
        periode_rapport_end: string | null;
        periode_rapport_start: string | null;
        rugligging_tot: string | null;
        rugligging_van: string | null;
        toelichting: string | null;
    };
    toelichting: string | null;
}

type PressureField = 'druk' | 'minimale_druk' | 'maximale_druk' | 'ninetyfive_druk' | 'druk_ipap' | 'druk_epap';

export function OsaseV2RequestAddEdit(props: ICustomControlRequestAddEditProps) {
    const [buttonDisabled, setButtonDisabled] = useState<boolean>();
    const [treatingPractitionerOptions, setTreatingPractitionerOptions] = useState<IOption<string>[]>([]);
    const [dateFieldsErrors, setDateFieldsErrors] = useState(emptyValidationErrors);
    const { patient } = useEpisodeContext();
    const [isCareProviderActive, setIsCareProviderActive] = useState<boolean | undefined>();

    useEffect(() => {
        loadIsPatientCareProviderActive();
        if (!props.request.id) {
            loadTreatingPractitionerOptionsAndDataFromLatestOsasConsult();
        } else {
            loadTreatingPractitionerOptions();
        }
    }, []);

    const customControlData: IOsaseV2RequestCustomControlData = props.request.customControlData;

    return (
        <NewOrEditRequestProcessContainer
            isFirstItem={props.isFirstItem}
            itemState={props.itemState}
            process={props.process}
            service={props.service}
            title={getServiceLabel(props.service.code, 'request', props.service.name)}
        >
            <FormGroupContext.Provider value={{ labelWidth: 'one-quarter', size: 'medium', variant: 'outline' }}>
                <Form
                    onSubmit={(e) => {
                        e.preventDefault();
                    }}
                    submitOnEnter={false}
                >
                    <FormContextProvider
                        value={{
                            customControlData: true,
                            errors: props.errors,
                            item: customControlData,
                            onChange: async (key, value) => await props.handleChangeCustomControl({ [key]: value }),
                            onDoValidate: async (key) => await props.validateOneCustomControl(key),
                            placeholderGroupName: 'contact',
                        }}
                    >
                        {isCareProviderActive === false && (
                            <CareProviderSelector
                                {...props}
                                entity={props.request}
                            />
                        )}
                        <FormGroup
                            hasErrors={
                                props.errors.has('customControlData.behandelend_arts_id') ||
                                props.errors.has('customControlData.behandelend_arts_name')
                            }
                            label="Behandelend arts"
                        >
                            <Select
                                error={getValidationErrorTranslation(
                                    props.errors.get('customControlData.behandelend_arts_id') ??
                                        props.errors.get('customControlData.behandelend_arts_name')
                                )}
                                isClearable={true}
                                onChange={async (option) => {
                                    await props.handleChangeCustomControl({
                                        behandelend_arts_id: option?.value ?? null,
                                        behandelend_arts_name: option?.label ?? null,
                                    });
                                }}
                                options={treatingPractitionerOptions}
                                placeholder="Selecteer een zorgverlener..."
                                value={treatingPractitionerOptions.find(
                                    (option) => option.value === customControlData.behandelend_arts_id
                                )}
                            />
                        </FormGroup>

                        <FormGroup label="Behandeling">
                            <Box
                                display="flex"
                                flexDirection="row"
                                flexWrap="wrap"
                                gap={3}
                            >
                                {renderBehandelingOptions()}
                            </Box>
                        </FormGroup>
                        {Object.values(customControlData.behandeling).includes(true) && (
                            <>
                                {customControlData.behandeling.pap && (
                                    <>
                                        <FormSection title="PAP rapport">
                                            <FormGroup
                                                hasErrors={dateFieldsErrors.has(
                                                    'customControlData.pap.periode_rapport_end'
                                                )}
                                                label="Periode rapport"
                                            >
                                                <Spacer
                                                    direction="horizontal"
                                                    wrap="wrap"
                                                >
                                                    <IndependentDateInput
                                                        hasMaxWidth={true}
                                                        maxDate={
                                                            customControlData.pap.periode_rapport_end
                                                                ? DateTime.from(
                                                                      customControlData.pap.periode_rapport_end
                                                                  )
                                                                      .subtract(1, 'day')
                                                                      .format('YYYY-MM-DD')
                                                                : null
                                                        }
                                                        onChange={async (value) => {
                                                            await props.handleChangeCustomControl({
                                                                pap: {
                                                                    ...customControlData.pap,
                                                                    periode_rapport_start: value,
                                                                },
                                                            });

                                                            validateEndDateIsAfterStartDate(
                                                                value,
                                                                customControlData.pap.periode_rapport_end,
                                                                'customControlData.pap.periode_rapport_end'
                                                            );
                                                        }}
                                                        onDoValidate={async () => {
                                                            await props.validateOneCustomControl(
                                                                'pap.periode_rapport_start'
                                                            );
                                                        }}
                                                        placeholder="dd-mm-jjjj"
                                                        value={customControlData.pap.periode_rapport_start}
                                                        variant="outline"
                                                    />
                                                    <Text
                                                        paddingTop={2}
                                                        size="sm"
                                                    >
                                                        t/m
                                                    </Text>
                                                    <IndependentDateInput
                                                        error={dateFieldsErrors.get(
                                                            'customControlData.pap.periode_rapport_end'
                                                        )}
                                                        hasMaxWidth={true}
                                                        minDate={
                                                            customControlData.pap.periode_rapport_start
                                                                ? DateTime.from(
                                                                      customControlData.pap.periode_rapport_start
                                                                  )
                                                                      .add(1, 'day')
                                                                      .format('YYYY-MM-DD')
                                                                : null
                                                        }
                                                        onChange={async (value) => {
                                                            await props.handleChangeCustomControl({
                                                                pap: {
                                                                    ...customControlData.pap,
                                                                    periode_rapport_end: value,
                                                                },
                                                            });

                                                            validateEndDateIsAfterStartDate(
                                                                customControlData.pap.periode_rapport_start,
                                                                value,
                                                                'customControlData.pap.periode_rapport_end'
                                                            );
                                                        }}
                                                        onDoValidate={async () =>
                                                            await props.validateOneCustomControl(
                                                                'pap.periode_rapport_end'
                                                            )
                                                        }
                                                        placeholder="dd-mm-jjjj"
                                                        value={customControlData.pap.periode_rapport_end}
                                                        variant="outline"
                                                    />
                                                </Spacer>
                                            </FormGroup>
                                            <FormGroup label="Rapport">
                                                {renderAssetDropzoneWithPlacement(osaseFilePlacement.papReport)}
                                            </FormGroup>
                                        </FormSection>

                                        <FormSection title="Apparaat en instellingen">
                                            <FormGroup label="Type">
                                                <RadioGroup
                                                    name="papType"
                                                    onChange={async (event) => {
                                                        const value = event.target.value;

                                                        await props.handleChangeCustomControl({
                                                            pap: {
                                                                ...customControlData.pap,
                                                                druk: emptyOsasePressure,
                                                                druk_epap: emptyOsasePressure,
                                                                druk_ipap: emptyOsasePressure,
                                                                maximale_druk: emptyOsasePressure,
                                                                minimale_druk: emptyOsasePressure,
                                                                ninetyfive_druk: emptyOsasePressure,
                                                                type: value,
                                                            },
                                                        });
                                                    }}
                                                    value={customControlData.pap.type?.toString() ?? ''}
                                                >
                                                    <Radio
                                                        label={OsasPapType.cpap}
                                                        value={OsasPapType.cpap}
                                                    />
                                                    <Radio
                                                        label={OsasPapType.apap}
                                                        value={OsasPapType.apap}
                                                    />
                                                    <Radio
                                                        label={OsasPapType.biPap}
                                                        value={OsasPapType.biPap}
                                                    />
                                                </RadioGroup>
                                            </FormGroup>

                                            {customControlData.pap.type === OsasPapType.cpap &&
                                                renderPressureFormGroup('Druk', 'druk')}
                                            {customControlData.pap.type === OsasPapType.apap && (
                                                <>
                                                    {renderPressureFormGroup('Minimale druk', 'minimale_druk')}
                                                    {renderPressureFormGroup('Maximale druk', 'maximale_druk')}
                                                    {renderPressureFormGroup('95% druk', 'ninetyfive_druk')}
                                                </>
                                            )}

                                            {customControlData.pap.type === OsasPapType.biPap && (
                                                <>
                                                    {renderPressureFormGroup('Druk IPAP (Inhale)', 'druk_ipap')}
                                                    {renderPressureFormGroup('Druk EPAP (Exhale)', 'druk_epap')}
                                                </>
                                            )}
                                        </FormSection>
                                        <FormSection title="Gebruik">
                                            {renderTextInputFormGroup(
                                                'Nachten met ondersteuning',
                                                customControlData.pap.nachten_met_ondersteuning,
                                                async (event) => {
                                                    const value = event.target.value || null;
                                                    await props.handleChangeCustomControl({
                                                        pap: {
                                                            ...customControlData.pap,
                                                            nachten_met_ondersteuning: value,
                                                        },
                                                    });
                                                },
                                                'pap.nachten_met_ondersteuning'
                                            )}
                                            {renderTextInputFormGroup(
                                                'Gemiddeld gebruik',
                                                customControlData.pap.gemiddeld_gebruik,
                                                async (event) => {
                                                    const value = event.target.value || null;
                                                    await props.handleChangeCustomControl({
                                                        pap: { ...customControlData.pap, gemiddeld_gebruik: value },
                                                    });
                                                },
                                                'pap.gemiddeld_gebruik',
                                                'uur / nacht'
                                            )}
                                        </FormSection>
                                        <FormSection title="Waarden">
                                            {renderTextInputFormGroup(
                                                'pAHI',
                                                customControlData.pap.ahi,
                                                async (event) => {
                                                    const value = event.target.value || null;
                                                    await props.handleChangeCustomControl({
                                                        pap: { ...customControlData.pap, ahi: value },
                                                    });
                                                },
                                                'pap.ahi',
                                                '/ uur'
                                            )}
                                            {renderTextInputFormGroup(
                                                'Obstructief apneu',
                                                customControlData.pap.obstructief_apneu,
                                                async (event) => {
                                                    const value = event.target.value || null;
                                                    await props.handleChangeCustomControl({
                                                        pap: { ...customControlData.pap, obstructief_apneu: value },
                                                    });
                                                },
                                                'pap.obstructief_apneu',
                                                '/ uur'
                                            )}
                                            {renderTextInputFormGroup(
                                                'Centraal apneu',
                                                customControlData.pap.centraal_apneu,
                                                async (event) => {
                                                    const value = event.target.value || null;
                                                    await props.handleChangeCustomControl({
                                                        pap: { ...customControlData.pap, centraal_apneu: value },
                                                    });
                                                },
                                                'pap.centraal_apneu',
                                                '/ uur'
                                            )}
                                            {renderTextInputFormGroup(
                                                'Cheyne-stokes',
                                                customControlData.pap.cheyne_stokes,
                                                async (event) => {
                                                    const value = event.target.value || null;
                                                    await props.handleChangeCustomControl({
                                                        pap: { ...customControlData.pap, cheyne_stokes: value },
                                                    });
                                                },
                                                'pap.cheyne_stokes'
                                            )}
                                            <FormGroup
                                                hasErrors={props.errors.has('customControlData.pap.spo2')}
                                                label="SPO2"
                                            >
                                                <TextArea
                                                    error={getValidationErrorTranslation(
                                                        props.errors.get('customControlData.pap.spo2')
                                                    )}
                                                    onChange={async (event) => {
                                                        const value = event.target.value || null;
                                                        await props.handleChangeCustomControl({
                                                            pap: { ...customControlData.pap, spo2: value },
                                                        });
                                                    }}
                                                    value={customControlData.pap.spo2 || ''}
                                                    variant="outline"
                                                />
                                            </FormGroup>
                                            <FormGroup
                                                hasErrors={props.errors.has(
                                                    'customControlData.pap.extra_waarden_uit_rapport'
                                                )}
                                                label="Extra waarden uit rapport"
                                            >
                                                <TextArea
                                                    error={getValidationErrorTranslation(
                                                        props.errors.get(
                                                            'customControlData.pap.extra_waarden_uit_rapport'
                                                        )
                                                    )}
                                                    onChange={async (event) => {
                                                        const value = event.target.value || null;
                                                        await props.handleChangeCustomControl({
                                                            pap: {
                                                                ...customControlData.pap,
                                                                extra_waarden_uit_rapport: value,
                                                            },
                                                        });
                                                    }}
                                                    value={customControlData.pap.extra_waarden_uit_rapport || ''}
                                                    variant="outline"
                                                />
                                            </FormGroup>
                                        </FormSection>
                                    </>
                                )}

                                {customControlData.behandeling.spt && (
                                    <>
                                        <FormSection title="SPT rapport">
                                            <FormGroup
                                                hasErrors={dateFieldsErrors.has(
                                                    'customControlData.spt.periode_rapport_end'
                                                )}
                                                label="Periode rapport"
                                            >
                                                <Spacer
                                                    direction="horizontal"
                                                    wrap="wrap"
                                                >
                                                    <IndependentDateInput
                                                        hasMaxWidth={true}
                                                        maxDate={
                                                            customControlData.spt.periode_rapport_end
                                                                ? DateTime.from(
                                                                      customControlData.spt.periode_rapport_end
                                                                  )
                                                                      .subtract(1, 'day')
                                                                      .format('YYYY-MM-DD')
                                                                : null
                                                        }
                                                        onChange={async (value) => {
                                                            await props.handleChangeCustomControl({
                                                                spt: {
                                                                    ...customControlData.spt,
                                                                    periode_rapport_start: value,
                                                                },
                                                            });

                                                            validateEndDateIsAfterStartDate(
                                                                value,
                                                                customControlData.spt.periode_rapport_end,
                                                                'customControlData.spt.periode_rapport_end'
                                                            );
                                                        }}
                                                        placeholder="dd-mm-jjjj"
                                                        value={customControlData.spt.periode_rapport_start}
                                                        variant="outline"
                                                    />
                                                    <Text
                                                        paddingTop={2}
                                                        size="sm"
                                                    >
                                                        t/m
                                                    </Text>
                                                    <IndependentDateInput
                                                        error={dateFieldsErrors.get(
                                                            'customControlData.spt.periode_rapport_end'
                                                        )}
                                                        hasMaxWidth={true}
                                                        minDate={
                                                            customControlData.spt.periode_rapport_start
                                                                ? DateTime.from(
                                                                      customControlData.spt.periode_rapport_start
                                                                  )
                                                                      .add(1, 'day')
                                                                      .format('YYYY-MM-DD')
                                                                : null
                                                        }
                                                        onChange={async (value) => {
                                                            await props.handleChangeCustomControl({
                                                                spt: {
                                                                    ...customControlData.spt,
                                                                    periode_rapport_end: value,
                                                                },
                                                            });
                                                            validateEndDateIsAfterStartDate(
                                                                customControlData.spt.periode_rapport_start,
                                                                value,
                                                                'customControlData.spt.periode_rapport_end'
                                                            );
                                                        }}
                                                        placeholder="dd-mm-jjjj"
                                                        value={customControlData.spt.periode_rapport_end}
                                                        variant="outline"
                                                    />
                                                </Spacer>
                                            </FormGroup>
                                            <FormGroup label="Rapport">
                                                {renderAssetDropzoneWithPlacement(osaseFilePlacement.sptReport)}
                                            </FormGroup>
                                        </FormSection>
                                        <FormSection title="Gebruik">
                                            {renderTextInputFormGroup(
                                                'Nachten met ondersteuning',
                                                customControlData.spt.nachten_met_ondersteuning,
                                                async (event) => {
                                                    const value = event.target.value || null;
                                                    await props.handleChangeCustomControl({
                                                        spt: {
                                                            ...customControlData.spt,
                                                            nachten_met_ondersteuning: value,
                                                        },
                                                    });
                                                },
                                                'spt.nachten_met_ondersteuning'
                                            )}
                                            {renderTextInputFormGroup(
                                                'Gemiddeld gebruik',
                                                customControlData.spt.gemiddeld_gebruik,
                                                async (event) => {
                                                    const value = event.target.value || null;
                                                    await props.handleChangeCustomControl({
                                                        spt: { ...customControlData.spt, gemiddeld_gebruik: value },
                                                    });
                                                },
                                                'spt.gemiddeld_gebruik',
                                                'uur / nacht'
                                            )}
                                        </FormSection>
                                        <FormSection title="Waarden">
                                            <FormGroup
                                                hasErrors={
                                                    props.errors.has('customControlData.spt.rugligging_van') ||
                                                    props.errors.has('customControlData.spt.rugligging_tot')
                                                }
                                                label="Rugligging"
                                            >
                                                <Row columnGap={1}>
                                                    <Col span={1}>
                                                        <Text
                                                            paddingTop={2}
                                                            size="sm"
                                                        >
                                                            van
                                                        </Text>
                                                    </Col>
                                                    <Col span={3}>
                                                        <TextInput
                                                            error={getValidationErrorTranslation(
                                                                props.errors.get('customControlData.spt.rugligging_van')
                                                            )}
                                                            onBlur={async () =>
                                                                await props.validateOneCustomControl(
                                                                    'spt.rugligging_van'
                                                                )
                                                            }
                                                            onChange={async (event) => {
                                                                const value = event.target.value || null;
                                                                await props.handleChangeCustomControl({
                                                                    spt: {
                                                                        ...customControlData.spt,
                                                                        rugligging_van: value,
                                                                    },
                                                                });
                                                            }}
                                                            value={customControlData.spt.rugligging_van || ''}
                                                            variant="outline"
                                                        />
                                                    </Col>
                                                    <Col span={1}>
                                                        <Text
                                                            paddingTop={2}
                                                            size="sm"
                                                        >
                                                            %
                                                        </Text>
                                                    </Col>
                                                    <Col span={1}>
                                                        <Text
                                                            paddingTop={2}
                                                            size="sm"
                                                        >
                                                            naar
                                                        </Text>
                                                    </Col>
                                                    <Col span={3}>
                                                        <TextInput
                                                            error={getValidationErrorTranslation(
                                                                props.errors.get('customControlData.spt.rugligging_tot')
                                                            )}
                                                            onBlur={async () =>
                                                                await props.validateOneCustomControl(
                                                                    'spt.rugligging_tot'
                                                                )
                                                            }
                                                            onChange={async (event) => {
                                                                const value = event.target.value || null;
                                                                await props.handleChangeCustomControl({
                                                                    spt: {
                                                                        ...customControlData.spt,
                                                                        rugligging_tot: value,
                                                                    },
                                                                });
                                                            }}
                                                            value={customControlData.spt.rugligging_tot || ''}
                                                            variant="outline"
                                                        />
                                                    </Col>
                                                    <Col span={1}>
                                                        <Text
                                                            paddingTop={2}
                                                            size="sm"
                                                        >
                                                            %
                                                        </Text>
                                                    </Col>
                                                </Row>
                                            </FormGroup>

                                            <FormGroup
                                                hasErrors={props.errors.has('customControlData.spt.toelichting')}
                                                label="Toelichting"
                                            >
                                                <TextArea
                                                    error={getValidationErrorTranslation(
                                                        props.errors.get('customControlData.spt.toelichting')
                                                    )}
                                                    onChange={async (event) => {
                                                        const value = event.target.value || null;
                                                        await props.handleChangeCustomControl({
                                                            spt: {
                                                                ...customControlData.spt,
                                                                toelichting: value,
                                                            },
                                                        });
                                                    }}
                                                    value={customControlData.spt.toelichting || ''}
                                                    variant="outline"
                                                />
                                            </FormGroup>
                                        </FormSection>
                                    </>
                                )}

                                {customControlData.behandeling.mra && displayMraFields()}

                                {customControlData.behandeling.leefstijl_verandering && displayLeefstijlFields()}

                                {(customControlData.behandeling.spt ||
                                    customControlData.behandeling.pap ||
                                    (customControlData.behandeling.leefstijl_verandering &&
                                        customControlData.behandeling.mra)) && (
                                    <FormSection title="Anamnese">
                                        {(customControlData.behandeling.mra ||
                                            customControlData.behandeling.spt ||
                                            customControlData.behandeling.pap) &&
                                            displayHadContactWithPatient()}

                                        {displayAnamneseField()}

                                        {displayHadContactWithPatientExtraFields()}
                                    </FormSection>
                                )}
                                <FormSection title="Beleid">
                                    <FormGroup
                                        hasErrors={props.errors.has('customControlData.beleid')}
                                        label="Beleid"
                                    >
                                        <TextArea
                                            error={getValidationErrorTranslation(
                                                props.errors.get('customControlData.beleid')
                                            )}
                                            onChange={async (event) => {
                                                const value = event.target.value || null;
                                                await props.handleChangeCustomControl({
                                                    beleid: value,
                                                });
                                            }}
                                            value={customControlData.beleid || ''}
                                            variant="outline"
                                        />
                                    </FormGroup>
                                </FormSection>

                                <FormSection title="Informeren">
                                    <FormGroup label="Huisarts op de hoogte brengen">
                                        <RadioGroup
                                            layout="row"
                                            name="huisarts_op_de_hoogte_brengen"
                                            onChange={async (event) => {
                                                await props.handleChangeCustomControl({
                                                    huisarts_op_de_hoogte_brengen: event.target.value === 'true',
                                                });
                                            }}
                                            value={String(customControlData.huisarts_op_de_hoogte_brengen)}
                                        >
                                            <Radio
                                                label="Ja"
                                                value="true"
                                            />
                                            <Radio
                                                label="Nee"
                                                value="false"
                                            />
                                        </RadioGroup>
                                    </FormGroup>
                                </FormSection>
                            </>
                        )}
                    </FormContextProvider>

                    <ButtonContainer
                        alignRight
                        buttons={[
                            <CancelButton
                                cancel={props.onCancel}
                                entity={ProcessEntity.request}
                                isExisting={!!props.request.id}
                                key="cancel"
                                serviceCode={props.service.code}
                                serviceType={props.service.serviceType}
                            />,
                            <SaveButton
                                buttonDisabled={buttonDisabled}
                                key="save"
                                save={async () => {
                                    await saveRequest('draft');
                                }}
                            />,
                            <SaveSendButton
                                buttonDisabled={buttonDisabled}
                                key="saveSend"
                                saveSend={async () => {
                                    await saveRequest('final');
                                }}
                            />,
                        ]}
                    />
                </Form>
            </FormGroupContext.Provider>
        </NewOrEditRequestProcessContainer>
    );

    function renderBehandelingOptions() {
        const options: JSX.Element[] = [];

        Object.entries(behandelingLabels).map(([key, label]) => {
            return options.push(
                <Choicebox
                    checked={customControlData.behandeling[key as keyof IOsaseBehandeling]}
                    key={key}
                    label={label}
                    name={key}
                    onChange={async () => {
                        const value = !customControlData.behandeling[key as keyof IOsaseBehandeling];

                        const behandelingUpdate = {
                            ...customControlData.behandeling,
                            [key]: value,
                        };

                        const customControlDataUpdate = !value
                            ? getClearedBehandelingFields(key as keyof IOsaseBehandeling, behandelingUpdate)
                            : {
                                  contact_met_patient_gehad:
                                      customControlData.contact_met_patient_gehad === null &&
                                      key !== 'leefstijl_verandering'
                                          ? true
                                          : customControlData.contact_met_patient_gehad,
                                  huisarts_op_de_hoogte_brengen:
                                      customControlData.huisarts_op_de_hoogte_brengen === null
                                          ? false
                                          : customControlData.huisarts_op_de_hoogte_brengen,
                              };

                        await props.handleChangeCustomControl({
                            behandeling: behandelingUpdate,
                            ...customControlDataUpdate,
                        });

                        if (key === 'spt' && !value) {
                            setDateFieldsErrors(dateFieldsErrors.remove('customControlData.spt.periode_rapport_end'));
                        } else if (key === 'pap' && !value) {
                            setDateFieldsErrors(dateFieldsErrors.remove('customControlData.pap.periode_rapport_end'));
                        }
                    }}
                />
            );
        });

        return options;
    }

    function displayLeefstijlFields() {
        const shouldDisplayAnamnese = !(
            customControlData.behandeling.mra ||
            customControlData.behandeling.spt ||
            customControlData.behandeling.pap
        );

        return (
            <FormSection title={shouldDisplayAnamnese ? 'Anamnese' : behandelingLabels.leefstijl_verandering}>
                <FormGroup label="Volgt patiënt het beleid?">
                    <RadioGroup
                        layout="row"
                        name="leefstijl_volgt_patient_het_beleid"
                        onChange={async (event) => {
                            await props.handleChangeCustomControl({
                                leefstijl_volgt_patient_het_beleid: event.target.value,
                            });
                        }}
                        value={customControlData.leefstijl_volgt_patient_het_beleid?.toString() ?? ''}
                    >
                        <Radio
                            label={YesOrNoOrUnknown.yes}
                            value={YesOrNoOrUnknown.yes}
                        />
                        <Radio
                            label={YesOrNoOrUnknown.no}
                            value={YesOrNoOrUnknown.no}
                        />
                        <Radio
                            label={YesOrNoOrUnknown.unknown}
                            value={YesOrNoOrUnknown.unknown}
                        />
                    </RadioGroup>
                </FormGroup>
                {shouldDisplayAnamnese && displayAnamneseField()}
                <FormGroup label="Bijlage">
                    {renderAssetDropzoneWithPlacement(osaseFilePlacement.leefstijlAttachment)}
                </FormGroup>
            </FormSection>
        );
    }

    function displayMraFields() {
        const shouldDisplayAnamnese = !(
            customControlData.behandeling.leefstijl_verandering ||
            customControlData.behandeling.spt ||
            customControlData.behandeling.pap
        );

        return (
            <FormSection title={shouldDisplayAnamnese ? 'Anamnese' : behandelingLabels.mra}>
                {shouldDisplayAnamnese && displayHadContactWithPatient()}

                {(customControlData.contact_met_patient_gehad || !shouldDisplayAnamnese) && (
                    <FormGroup label="Volgt patiënt het beleid?">
                        <RadioGroup
                            layout="row"
                            name="mra_volgt_patient_het_beleid"
                            onChange={async (event) => {
                                await props.handleChangeCustomControl({
                                    mra_volgt_patient_het_beleid: event.target.value,
                                });
                            }}
                            value={customControlData.mra_volgt_patient_het_beleid?.toString() ?? ''}
                        >
                            <Radio
                                label={YesOrNoOrUnknown.yes}
                                value={YesOrNoOrUnknown.yes}
                            />
                            <Radio
                                label={YesOrNoOrUnknown.no}
                                value={YesOrNoOrUnknown.no}
                            />
                            <Radio
                                label={YesOrNoOrUnknown.unknown}
                                value={YesOrNoOrUnknown.unknown}
                            />
                        </RadioGroup>
                    </FormGroup>
                )}

                {shouldDisplayAnamnese && (
                    <>
                        {displayAnamneseField()}
                        {displayHadContactWithPatientExtraFields()}
                    </>
                )}

                <FormGroup label="Bijlage">
                    {renderAssetDropzoneWithPlacement(osaseFilePlacement.mraAttachment)}
                </FormGroup>
            </FormSection>
        );
    }

    function displayAnamneseField() {
        return (
            <FormGroup
                hasErrors={props.errors.has('customControlData.anamnese')}
                label="Anamnese"
            >
                <TextArea
                    error={getValidationErrorTranslation(props.errors.get('customControlData.anamnese'))}
                    onChange={async (event) => {
                        const value = event.target.value || null;
                        await props.handleChangeCustomControl({
                            anamnese: value,
                        });
                    }}
                    value={customControlData.anamnese || ''}
                    variant="outline"
                />
            </FormGroup>
        );
    }

    function renderAssetDropzoneWithPlacement(placement: osaseFilePlacement) {
        return (
            <AssetDropzoneWithActions
                acceptedFiles={osaseFileExtensions}
                additionalFilesCheckboxText={getSelectedLanguage().request.attachment}
                assets={getFilesByPlacement(customControlData.files, placement)}
                assetsBaseUrl={FilesUrlBase.requests}
                metadata={{ placement }}
                onChange={async (assets) => {
                    const filesWithPlacement: IOsaseAsset[] = assets.map((asset) => {
                        return { ...asset, placement };
                    });
                    await props.handleChangeCustomControl({
                        files: [...filesWithPlacement, ...removeAllFilesWithPlacement(placement)],
                    });
                }}
                onUploading={async (isUploading) => setButtonDisabled(isUploading)}
                processActivityId={props.request.id}
                service={props.service}
                showAdditionalFilesCheckbox={props.previousRequest !== undefined}
                updateNewRequestPreviousRequest={props.updateNewRequestPreviousRequest}
            />
        );
    }

    function renderTextInputFormGroup(
        label: string,
        value: string | null,
        onChange: React.ChangeEventHandler<HTMLInputElement>,
        fieldName: string,
        suffixText?: string
    ) {
        return (
            <FormGroup
                hasErrors={props.errors.has(`customControlData.${fieldName}`)}
                label={label}
            >
                <Spacer
                    direction="horizontal"
                    wrap="wrap"
                >
                    <TextInput
                        error={getValidationErrorTranslation(props.errors.get(`customControlData.${fieldName}`))}
                        id="text-input"
                        onBlur={async () => await props.validateOneCustomControl(fieldName)}
                        onChange={onChange}
                        value={value || ''}
                        variant="outline"
                    />
                    <Text
                        paddingTop={2}
                        size="sm"
                    >
                        {suffixText}
                    </Text>
                </Spacer>
            </FormGroup>
        );
    }

    function renderPressureFormGroup(label: string, fieldName: PressureField) {
        const pressure: IOsasePressure = customControlData.pap[fieldName];
        const textInputWidth = document.getElementById('text-input')?.offsetWidth ?? 0;

        const pressureTextInput = (
            <div style={{ marginLeft: '32px', width: `${textInputWidth - 32}px` }}>
                <TextInput
                    error={getValidationErrorTranslation(props.errors.get(`customControlData.pap.${fieldName}.value`))}
                    onBlur={async () => {
                        await props.validateOneCustomControl(`pap.${fieldName}.value`);
                    }}
                    onChange={async (event) => {
                        const newValue = event.target.value;

                        await props.handleChangeCustomControl({
                            pap: {
                                ...customControlData.pap,
                                [fieldName]: {
                                    ...customControlData.pap[fieldName],
                                    value: newValue,
                                },
                            },
                        });
                    }}
                    value={pressure.value || ''}
                    variant="outline"
                />
            </div>
        );

        return (
            <FormGroup
                hasErrors={
                    props.errors.has(`customControlData.pap.${fieldName}.value`) ||
                    props.errors.has(`customControlData.pap.${fieldName}.unit`)
                }
                label={label}
            >
                <RadioGroup
                    error={getValidationErrorTranslation(props.errors.get(`customControlData.pap.${fieldName}.unit`))}
                    name={fieldName}
                    onChange={async (event) => {
                        const newUnit = event.target.value as osasePressureUnit;

                        await props.handleChangeCustomControl({
                            pap: {
                                ...customControlData.pap,
                                [fieldName]: {
                                    ...customControlData.pap[fieldName],
                                    unit: newUnit,
                                },
                            },
                        });
                        await props.validateOneCustomControl(`pap.${fieldName}.unit`);
                    }}
                    onClearValue={async () => {
                        await props.handleChangeCustomControl({
                            pap: {
                                ...customControlData.pap,
                                [fieldName]: emptyOsasePressure,
                            },
                        });
                        await props.validateOneCustomControl(`pap.${fieldName}.value`);
                        await props.validateOneCustomControl(`pap.${fieldName}.unit`);
                    }}
                    value={pressure.unit as string}
                >
                    <Radio
                        label={osasePressureUnitOptions.cmH2O}
                        value={osasePressureUnitOptions.cmH2O}
                    />
                    <>{pressure.unit === osasePressureUnitOptions.cmH2O && pressureTextInput}</>
                    <Radio
                        label={osasePressureUnitOptions.hPa}
                        value={osasePressureUnitOptions.hPa}
                    />
                    <>{pressure.unit === osasePressureUnitOptions.hPa && pressureTextInput}</>
                </RadioGroup>
            </FormGroup>
        );
    }

    function displayHadContactWithPatient() {
        return (
            <FormGroup label="Contact met patiënt gehad?">
                <RadioGroup
                    layout="row"
                    name="contact_met_patient_gehad"
                    onChange={async (event) => {
                        const value = event.target.value === 'true';

                        const isMraTheOnlySelectedTreatment =
                            !(
                                customControlData.behandeling.leefstijl_verandering ||
                                customControlData.behandeling.spt ||
                                customControlData.behandeling.pap
                            ) && customControlData.behandeling.mra;

                        const mra_volgt_patient_het_beleid =
                            !value && isMraTheOnlySelectedTreatment
                                ? null
                                : customControlData.mra_volgt_patient_het_beleid;

                        const customControlDataUpdate = !value
                            ? {
                                  klachtenlast: null,
                                  proefperiode: null,
                                  toelichting: null,
                              }
                            : null;

                        await props.handleChangeCustomControl({
                            contact_met_patient_gehad: value,
                            mra_volgt_patient_het_beleid,
                            ...customControlDataUpdate,
                        });
                    }}
                    value={String(customControlData.contact_met_patient_gehad)}
                >
                    <Radio
                        label="Ja"
                        value="true"
                    />
                    <Radio
                        label="Nee"
                        value="false"
                    />
                </RadioGroup>
            </FormGroup>
        );
    }

    function displayHadContactWithPatientExtraFields() {
        if (customControlData.contact_met_patient_gehad) {
            return (
                <>
                    <FormGroup label="Klachtenlast">
                        <RadioGroup
                            name="klachtenlast"
                            onChange={async (event) => {
                                await props.handleChangeCustomControl({
                                    klachtenlast: event.target.value,
                                });
                            }}
                            onClearValue={async () => await props.handleChangeCustomControl({ klachtenlast: null })}
                            value={customControlData.klachtenlast?.toString() ?? ''}
                        >
                            <Radio
                                label={OsasComplaintBurden.decreased}
                                value={OsasComplaintBurden.decreased}
                            />
                            <Radio
                                label={OsasComplaintBurden.stayedSame}
                                value={OsasComplaintBurden.stayedSame}
                            />
                            <Radio
                                label={OsasComplaintBurden.increased}
                                value={OsasComplaintBurden.increased}
                            />
                        </RadioGroup>
                    </FormGroup>
                    {(customControlData.behandeling.pap || customControlData.behandeling.spt) && (
                        <FormGroup label="Proefperiode">
                            <RadioGroup
                                name="proefperiode"
                                onChange={async (event) => {
                                    await props.handleChangeCustomControl({
                                        proefperiode: event.target.value,
                                    });
                                }}
                                onClearValue={async () => await props.handleChangeCustomControl({ proefperiode: null })}
                                value={customControlData.proefperiode?.toString() ?? ''}
                            >
                                <Radio
                                    label={OsasTrialPeriod.successful}
                                    value={OsasTrialPeriod.successful}
                                />
                                <Radio
                                    label={OsasTrialPeriod.continue}
                                    value={OsasTrialPeriod.continue}
                                />
                                <Radio
                                    label={OsasTrialPeriod.stop}
                                    value={OsasTrialPeriod.stop}
                                />
                            </RadioGroup>
                        </FormGroup>
                    )}

                    <FormGroup
                        hasErrors={props.errors.has('customControlData.toelichting')}
                        label="Toelichting"
                    >
                        <TextArea
                            error={getValidationErrorTranslation(props.errors.get('customControlData.toelichting'))}
                            onChange={async (event) => {
                                const value = event.target.value || null;
                                await props.handleChangeCustomControl({
                                    toelichting: value,
                                });
                            }}
                            value={customControlData.toelichting || ''}
                            variant="outline"
                        />
                    </FormGroup>
                </>
            );
        }
    }

    function getClearedBehandelingFields(field: keyof IOsaseBehandeling, behandelingUpdate: IOsaseBehandeling) {
        const shouldClearCommonFields = !Object.values(behandelingUpdate).includes(true);

        const commonFields = shouldClearCommonFields
            ? {
                  anamnese: null,
                  beleid: null,
                  huisarts_op_de_hoogte_brengen: null,
              }
            : null;

        const shouldClearAnamneseFields = ![
            behandelingUpdate.mra,
            behandelingUpdate.spt,
            behandelingUpdate.pap,
        ].includes(true);

        const anamneseFields = shouldClearAnamneseFields
            ? {
                  contact_met_patient_gehad: null,
                  klachtenlast: null,
                  proefperiode: null,
                  toelichting: null,
              }
            : null;

        const shouldClearMraVolgtPatientHetBeleidField =
            ![behandelingUpdate.leefstijl_verandering, behandelingUpdate.spt, behandelingUpdate.pap].includes(true) &&
            !customControlData.contact_met_patient_gehad;

        const proefperiode = behandelingUpdate.spt || behandelingUpdate.pap ? customControlData.proefperiode : null;

        const mra_volgt_patient_het_beleid = shouldClearMraVolgtPatientHetBeleidField
            ? null
            : customControlData.mra_volgt_patient_het_beleid;

        switch (field) {
            case 'leefstijl_verandering':
                return {
                    files: removeAllFilesWithPlacement(osaseFilePlacement.leefstijlAttachment),
                    leefstijl_volgt_patient_het_beleid: null,
                    mra_volgt_patient_het_beleid,
                    ...commonFields,
                };
            case 'mra':
                return {
                    files: removeAllFilesWithPlacement(osaseFilePlacement.mraAttachment),
                    mra_volgt_patient_het_beleid: null,
                    ...commonFields,
                    ...anamneseFields,
                };
            case 'spt':
                return {
                    files: removeAllFilesWithPlacement(osaseFilePlacement.sptReport),
                    mra_volgt_patient_het_beleid,
                    proefperiode,
                    spt: {
                        gemiddeld_gebruik: null,
                        nachten_met_ondersteuning: null,
                        periode_rapport_end: null,
                        periode_rapport_start: null,
                        rugligging_tot: null,
                        rugligging_van: null,
                        toelichting: null,
                    },
                    ...commonFields,
                    ...anamneseFields,
                };
            case 'pap':
                return {
                    files: removeAllFilesWithPlacement(osaseFilePlacement.papReport),
                    mra_volgt_patient_het_beleid,
                    pap: {
                        ahi: null,
                        centraal_apneu: null,
                        cheyne_stokes: null,
                        druk: emptyOsasePressure,
                        druk_epap: emptyOsasePressure,
                        druk_ipap: emptyOsasePressure,
                        extra_waarden_uit_rapport: null,
                        gemiddeld_gebruik: null,
                        maximale_druk: emptyOsasePressure,
                        minimale_druk: emptyOsasePressure,
                        nachten_met_ondersteuning: null,
                        ninetyfive_druk: emptyOsasePressure,
                        obstructief_apneu: null,
                        periode_rapport_end: null,
                        periode_rapport_start: null,
                        spo2: null,
                        type: null,
                    },
                    proefperiode,
                    ...commonFields,
                    ...anamneseFields,
                };
            default:
                break;
        }
    }

    function removeAllFilesWithPlacement(placement: osaseFilePlacement) {
        return customControlData.files.filter((file) => file.placement !== placement);
    }

    function validateEndDateIsAfterStartDate(startDate: string | null, endDate: string | null, endDateName: string) {
        if (startDate && endDate && DateTime.from(startDate).isSameOrAfter(endDate)) {
            setDateFieldsErrors(dateFieldsErrors.set(endDateName, 'koi_endDate'));
        } else {
            setDateFieldsErrors(dateFieldsErrors.remove(endDateName));
        }
    }

    async function getTreatingPractitionerOptions(): Promise<IOption<string>[]> {
        const result = await fetchJson(
            `/api/employments/${props.organizationId}/practitioners?isActive=true&professions=${Profession.medicalSpecialist}`,
            'GET'
        );
        const employments = result.json.items;
        const options = employments.map((employment: { practitioner: IPractitioner }) => ({
            label: getDisplayName(employment.practitioner as IPerson),
            value: employment.practitioner.id,
        }));

        return options;
    }

    async function loadTreatingPractitionerOptions() {
        const options = await getTreatingPractitionerOptions();

        if (
            customControlData.behandelend_arts_id &&
            !options.find((option: IOption<string>) => option.value === customControlData.behandelend_arts_id)
        ) {
            await props.handleChangeCustomControl({
                behandelend_arts_id: null,
                behandelend_arts_name: null,
            });
        }

        setTreatingPractitionerOptions(options);
    }

    async function loadTreatingPractitionerOptionsAndDataFromLatestOsasConsult() {
        const practitionerOptions = await getTreatingPractitionerOptions();

        const previousSomnologyRequestsResult = await fetchJson(
            `/api/requestProcesses?patientId=${props.request.patientId}&episodeCategory=${EpisodeCategory.somnology}&orderByCreatedAtDesc=true`,
            'GET'
        );

        const latestOsasConsult = previousSomnologyRequestsResult.json.items.find(
            (item: { code: ServiceCode; status: RequestStatus }) =>
                [ServiceCode.OSASC2, ServiceCode.OSASVC].includes(item.code) && item.status === RequestStatus.completed
        );

        if (latestOsasConsult?.code === ServiceCode.OSASC2) {
            const latestOsasc2DiagnosticReport = latestOsasConsult.diagnosticReports[0];

            if (latestOsasc2DiagnosticReport) {
                const osasc2DiagnosticReportResult = await fetchJson(
                    `/api/diagnosticReports/${latestOsasc2DiagnosticReport.id}`,
                    'GET'
                );

                const osasc2PerformerPractitionerId = osasc2DiagnosticReportResult.json.performerPractitionerId;
                const treatingPractitioner = practitionerOptions.find(
                    (option) => option.value === osasc2PerformerPractitionerId
                );

                const osasc2Policy = osasc2DiagnosticReportResult?.json.customControlData.fields?.beleid;
                const leefstijl_verandering = osasc2Policy?.adviesLifestyleVerandering ?? false;
                const mra = osasc2Policy?.mra ?? false;
                const spt = osasc2Policy?.spt ?? false;
                const pap = osasc2Policy?.cpap ?? false;
                const contact_met_patient_gehad = mra || spt || pap ? true : null;
                const huisarts_op_de_hoogte_brengen = leefstijl_verandering || mra || spt || pap ? false : null;

                await props.handleChangeCustomControl({
                    behandelend_arts_id: treatingPractitioner?.value ?? null,
                    behandelend_arts_name: treatingPractitioner?.label ?? null,
                    behandeling: {
                        leefstijl_verandering,
                        mra,
                        pap,
                        spt,
                    },
                    contact_met_patient_gehad,
                    huisarts_op_de_hoogte_brengen,
                });
            }
        } else if (latestOsasConsult?.code === ServiceCode.OSASVC) {
            const osasvcCustomControlDataResult = await fetchJson(
                `/api/requests/${latestOsasConsult.id}/customControlData`,
                'GET'
            );

            const osasConsultPerformerPractitionerId = osasvcCustomControlDataResult.json.recipientPractitionerId;
            const treatingPractitioner = practitionerOptions.find(
                (option) => option.value === osasConsultPerformerPractitionerId
            );

            const osasvcCustomControlData: IOsasvcV2CustomControlData =
                osasvcCustomControlDataResult.json.customControlData;

            const leefstijl_verandering = osasvcCustomControlData.aanvullend_beleid?.advies_lifestyle ?? false;
            const mra =
                areOsasvcTherapiesSelected(osasvcCustomControlData, [OsasvcV2TherapieNamelijkOption.mra]) ?? false;
            const spt =
                areOsasvcTherapiesSelected(osasvcCustomControlData, [OsasvcV2TherapieNamelijkOption.spt]) ?? false;
            const pap =
                areOsasvcTherapiesSelected(osasvcCustomControlData, [
                    OsasvcV2TherapieNamelijkOption.cpap,
                    OsasvcV2TherapieNamelijkOption.biPap,
                ]) ?? false;

            const contact_met_patient_gehad = mra || spt || pap ? true : null;
            const huisarts_op_de_hoogte_brengen = leefstijl_verandering || mra || spt || pap ? false : null;

            await props.handleChangeCustomControl({
                behandelend_arts_id: treatingPractitioner?.value ?? null,
                behandelend_arts_name: treatingPractitioner?.label ?? null,
                behandeling: {
                    leefstijl_verandering,
                    mra,
                    pap,
                    spt,
                },
                contact_met_patient_gehad,
                huisarts_op_de_hoogte_brengen,
            });
        }

        setTreatingPractitionerOptions(practitionerOptions);
    }

    async function loadIsPatientCareProviderActive() {
        const isPatientCareProviderActive = await getCareProviderIsActive(
            patient.careProviderPractitionerId,
            patient.careProviderOrganizationId
        );
        setIsCareProviderActive(isPatientCareProviderActive);

        if (props.request?.draftCareProviderPractitionerId) {
            if (isPatientCareProviderActive) {
                if (
                    props.request?.draftCareProviderPractitionerId !== patient.careProviderPractitionerId ||
                    props.request?.draftCareProviderOrganizationId !== patient.careProviderOrganizationId
                ) {
                    await props.handleChange('draftCareProviderOrganizationId', patient.careProviderOrganizationId!);
                    await props.handleChange('draftCareProviderPractitionerId', patient.careProviderPractitionerId!);
                }
            } else {
                const isPatientDraftCareProviderActive = await getCareProviderIsActive(
                    props.request?.draftCareProviderPractitionerId,
                    props.request?.draftCareProviderOrganizationId
                );
                if (!isPatientDraftCareProviderActive) {
                    await props.handleChange('draftCareProviderOrganizationId', null);
                    await props.handleChange('draftCareProviderPractitionerId', null);
                }
            }
        }
    }

    function areOsasvcTherapiesSelected(
        osasvcCustomControlData: IOsasvcV2CustomControlData,
        therapies: OsasvcV2TherapieNamelijkOption[]
    ) {
        return (
            (osasvcCustomControlData.therapie === OsasvcV2CTherapieGestartOption.continue &&
                osasvcCustomControlData.therapie_gestart_namelijk &&
                therapies.includes(osasvcCustomControlData.therapie_gestart_namelijk)) ||
            (osasvcCustomControlData.nieuwe_therapie_starten_namelijk &&
                therapies.includes(osasvcCustomControlData.nieuwe_therapie_starten_namelijk))
        );
    }

    async function saveRequest(saveType: 'draft' | 'final') {
        setButtonDisabled(true);

        const validatedRequest = await props.validateRequest(saveType);

        if (!validatedRequest || dateFieldsErrors.size) {
            setButtonDisabled(false);

            if (dateFieldsErrors.size && props.formId) {
                scrollValidationErrorIntoCenterViewInScrollContainer(props.formId, dateFieldsErrors);
            }

            return;
        }

        const requestId = await props.onSaveRequest(saveType);

        if (saveType === 'final') {
            showServiceToast(
                ToastType.success,
                'toast.submitSuccess',
                props.service,
                'request',
                getSelectedLanguage().toast.submitDefaultMessage
            );
        } else {
            showServiceToast(
                ToastType.success,
                'toast.draftSuccess',
                props.service,
                'request',
                getSelectedLanguage().toast.draftDefaultMessage
            );
        }
        await props.onSave(requestId);
    }
}

export function getFilesByPlacement(files: IOsaseAsset[] | undefined, placement: osaseFilePlacement) {
    if (files?.length) {
        return files.filter((file: IOsaseAsset) => file.placement === placement);
    } else {
        return [];
    }
}

const emptyOsasePressure: IOsasePressure = { unit: null, value: null };

export function osaseV2RequestInitializeCustomControlData(): IOsaseV2RequestCustomControlData {
    return {
        anamnese: null,
        behandelend_arts_id: null,
        behandelend_arts_name: null,
        behandeling: {
            leefstijl_verandering: false,
            mra: false,
            pap: false,
            spt: false,
        },
        beleid: null,
        contact_met_patient_gehad: null,
        files: [],
        huisarts_op_de_hoogte_brengen: null,
        klachtenlast: null,
        leefstijl_volgt_patient_het_beleid: null,
        mra_volgt_patient_het_beleid: null,
        pap: {
            ahi: null,
            centraal_apneu: null,
            cheyne_stokes: null,
            druk: emptyOsasePressure,
            druk_epap: emptyOsasePressure,
            druk_ipap: emptyOsasePressure,
            extra_waarden_uit_rapport: null,
            gemiddeld_gebruik: null,
            maximale_druk: emptyOsasePressure,
            minimale_druk: emptyOsasePressure,
            nachten_met_ondersteuning: null,
            ninetyfive_druk: emptyOsasePressure,
            obstructief_apneu: null,
            periode_rapport_end: null,
            periode_rapport_start: null,
            spo2: null,
            type: null,
        },
        proefperiode: null,
        spt: {
            gemiddeld_gebruik: null,
            nachten_met_ondersteuning: null,
            periode_rapport_end: null,
            periode_rapport_start: null,
            rugligging_tot: null,
            rugligging_van: null,
            toelichting: null,
        },
        toelichting: null,
    };
}
